import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Stack, Tooltip, Typography, Unstable_Grid2 as Grid, useMediaQuery } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import HelpButton from 'components/pqrs/HelpButton';
import LastPaymentCounter from 'components/dashboard/client/counters/LastPaymentCounter';
import SessionFinishedTodayCounter from 'components/dashboard/client/counters/SessionFinishedTodayCounter';
import SessionToScheduleCounter from 'components/dashboard/client/counters/SessionToScheduleCounter';
import NextGroupSessionsList from 'components/dashboard/client/NextGroupSessionsList';
import CompletedGroupSessionsList from 'components/dashboard/client/CompletedGroupSessionsList';
import DashboardHero from 'components/dashboard/DashboardHero';
import useOrganizationBranding from 'hooks/useOrganizationBranding';
import FeaturedNewsCarousel from 'components/shared/carousels/FeaturedNewsCarousel';
import MobileNewsList from 'components/dashboard/client/MobileNewsList';
import SessionsCompletedTable from 'components/dashboard/trainer/SessionsCompletedTable';
import NextSessionsList from 'components/shared/lists/NextSessionsList';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import { newsHubFields } from 'common/constants/query-fields/news';
import { sessionDashboardClientFields } from 'common/constants/query-fields/session';
import requestStatus from 'common/constants/status';
import AppBarContext from 'context/AppBarContext';
import clientPaymentSlice from 'store/modules/clientPayment';
import newsSlice from 'store/modules/news';
import sessionSlice from 'store/modules/session';
import groupSessionSlice from 'store/modules/groupSession';
import LimitedDashboard from 'components/dashboard/client/LimitedDashboard';
import PlayWeekRoutinePreview from 'components/play/PlayWeekRoutinePreview';
import PlayTodayRoutineCard from 'components/play/PlayTodayRoutineCard';
import WellbeingCheckinCard from 'components/wellbeing/WellbeingCheckinCard';
import PlayPerformanceSummaryCard from 'components/play/PlayPerformanceSummaryCard';

function ClientDashboard() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['notFound', 'routes', 'tables', 'pqrs']);
  // Marca de la empresa del usuario. Sin empresa (o en la organización por defecto) devuelve
  // nulos y el hero cae al degradado azulado de siempre.
  const branding = useOrganizationBranding();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const sessionLabel = t('session.list', { ns: 'routes' }).toLowerCase();
  const emptySessionsMessage = t('emptyNoFilters', { type: sessionLabel, ns: 'tables' });

  const listItemsPerPage = 5;

  const firstName = useSelector((state) => state.auth.user.first_name);
  const role = useSelector((state) => state.auth.user.role);
  const platform = useSelector((state) => state.auth.user?.platform);
  const hasOneOnOne = useSelector((state) => state.auth.user?.has_one_on_one_sessions);
  const isOtrixPlay = platform === 'otrix_play';
  const isActivePlus = useSelector((state) => state.auth.user.is_active_plus);
  const membershipStatus = useSelector((state) => state.auth.user?.membership_status);
  const planId = useSelector((state) => state.auth.user?.plan_id?.id);
  const uiLanguage = useSelector((state) => state.settings.config.language);
  const userId = Number(useSelector((state) => state.auth.user.id));
  const newsItems = useSelector((state) => state.news.data.news) || [];
  const newsStatus = useSelector((state) => state.news.status.general);
  const [newsFellBack, setNewsFellBack] = useState(false);

  const activePayments = useSelector((state) => state.clientPayment.data.activePayments) || [];
  const hasNonActivePlusSessions = isActivePlus && activePayments.some((p) => {
    const name = p.plan_id?.name?.toLowerCase().replace(/\s+/g, '') || '';
    return !name.includes('activeplus');
  });

  // Header de la sección = "Inicio" (consistente con el resto de páginas y con el eyebrow
  // del hero). El saludo "Qué bueno verte…/Hola, {name}" vive dentro del DashboardHero.
  const pageTitle = t('home', { ns: 'routes' });

  useEffect(() => {
    if (isActivePlus) {
      dispatch(groupSessionSlice.actions.fetchMyGroupSessions());
    }
    if (!isActivePlus || hasNonActivePlusSessions) {
      dispatch(
        sessionSlice.actions.dashboard({
          fields: sessionDashboardClientFields,
          userId
        })
      );
    }
  }, [isActivePlus, hasNonActivePlusSessions, role, userId]);

  useEffect(() => {
    dispatch(clientPaymentSlice.actions.getLatest(userId));
    if (isActivePlus) {
      dispatch(clientPaymentSlice.actions.getActivePayments(userId));
    }
  }, [userId, isActivePlus]);

  useEffect(() => {
    setNewsFellBack(false);
    dispatch(
      newsSlice.actions.list({
        fields: newsHubFields,
        filters: { language: uiLanguage },
        pagination: { first: 20, page: 1 }
      })
    );
  }, [uiLanguage]);

  // Fallback de idioma: si no hay noticias en el idioma de la app, mostrar las disponibles
  // (en cualquier idioma) en vez de dejar la sección vacía.
  useEffect(() => {
    if (newsStatus === requestStatus.success && newsItems.length === 0 && !newsFellBack) {
      setNewsFellBack(true);
      dispatch(
        newsSlice.actions.list({
          fields: newsHubFields,
          pagination: { first: 20, page: 1 }
        })
      );
    }
  }, [newsStatus, newsItems.length, newsFellBack, dispatch]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Only truly new users (never assigned a plan) see the plan selection screen.
  // Returning inactive users (plan_id set but payment expired) see their normal dashboard.
  if (membershipStatus === 'INACTIVE' && !planId) {
    return <LimitedDashboard />;
  }

  return (
    <>
      <SEOPageTitle title={t('home', { ns: 'routes' })} />

      <Grid container spacing={{ xs: 2, lg: 4 }}>
        <Grid xs={12} order={0}>
          <DashboardHero
            backgroundImage={branding.cover}
            logoImage={branding.logo}
            eyebrow={t('home', { ns: 'routes' })}
            greeting={t('hello', { ns: 'common', defaultValue: 'Hola,' })}
            highlight={firstName}
            subtitle={t('dashboard.subtitle', {
              ns: 'routes',
              defaultValue: 'Este es tu resumen de hoy en OTRIX.'
            })}
          />
        </Grid>
        {/* Bienestar Corporativo: check-in semanal (solo miembros de organización, una vez
            por semana; el componente decide solo si aparece). */}
        <Grid xs={12} order={1}>
          <WellbeingCheckinCard />
        </Grid>
        {/* OTRIX Play: la card "Rutina de hoy" (con motor de ejecución) reemplaza el counter "Hoy". */}
        {isOtrixPlay && (
          <Grid xs={12} order={1}>
            <PlayTodayRoutineCard />
          </Grid>
        )}
        {/* Los tres contadores EN UNA FILA también en móvil: apilados empujaban las noticias
            fuera de la pantalla y había que scrollear para enterarse de lo que viene. Caben
            porque la tipografía y el icono se comprimen en xs (ver los Styled del contador). */}
        <Grid xs={12} container order={1} spacing={{ xs: 0.75, md: 4 }}>
          {!isOtrixPlay && (
            <Grid xs={4}>
              <SessionFinishedTodayCounter />
            </Grid>
          )}
          <Grid xs={isOtrixPlay ? 6 : 4}>
            <LastPaymentCounter />
          </Grid>
          <Grid xs={isOtrixPlay ? 6 : 4}>
            <SessionToScheduleCounter />
          </Grid>
        </Grid>
        {/* El arrastre de sesiones solo aplica a quien TIENE sesiones 1:1: en Play (sin un plan
            adicional que las otorgue) el aviso de las 4 sesiones no significa nada. Mismo guard
            que la lista de sesiones de abajo. */}
        {/* En móvil baja del todo (order 4): el orden que pidió el usuario es contadores →
            noticias → el resto. En desktop se queda arriba, donde no le estorba a nadie. */}
        {(!isOtrixPlay || hasOneOnOne) && (!isActivePlus || hasNonActivePlusSessions) && (
          <Grid xs={12} order={{ xs: 4, md: 1 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ p: 1.5, borderRadius: '14px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised' }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Tooltip title={t('helpHint', { ns: 'pqrs' })} arrow>
                  <InfoOutlined sx={{ color: 'gold.main', fontSize: 20, flexShrink: 0 }} />
                </Tooltip>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('carryOverHint', { ns: 'pqrs', defaultValue: 'Si te quedan 4 sesiones o menos y tu plan vence, las sumamos a tu próximo pago al reactivarlo (hasta un máximo de 4 sesiones).' })}
                </Typography>
              </Stack>
              <HelpButton variant="text" />
            </Stack>
          </Grid>
        )}
        <Grid xs={12} md={6} order={{ xs: 3, md: 2 }}>
          <Stack spacing={{ xs: 2, lg: 4 }}>
            {/* OTRIX Play: la rutina de la semana reemplaza a "próximos entrenamientos" 1:1. */}
            {isOtrixPlay && <PlayWeekRoutinePreview />}
            {isOtrixPlay && <PlayPerformanceSummaryCard />}
            {isActivePlus && (
              <NextGroupSessionsList emptyMessage={emptySessionsMessage} itemsPerPage={2} />
            )}
            {!isOtrixPlay && (!isActivePlus || hasNonActivePlusSessions) && (
              <NextSessionsList emptyMessage={emptySessionsMessage} itemsPerPage={listItemsPerPage} />
            )}
            {isActivePlus && (
              <CompletedGroupSessionsList emptyMessage={emptySessionsMessage} itemsPerPage={2} />
            )}
            {(!isOtrixPlay || hasOneOnOne) && (!isActivePlus || hasNonActivePlusSessions) && (
              <SessionsCompletedTable emptyMessage={emptySessionsMessage} itemsPerPage={listItemsPerPage} />
            )}
          </Stack>
        </Grid>
        <Grid xs={12} md={6} order={{ xs: 2, md: 3 }}>
          {/* Mismo destacado en todos los tamaños (el carrusel ya trae su layout mobile);
              en mobile se le suma debajo la lista compacta con el resto de noticias. */}
          <Stack spacing={2}>
            <FeaturedNewsCarousel />
            {isMobile && <MobileNewsList skipFeatured />}
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}

export default ClientDashboard;
